import { useState } from 'react';
import { InventoryItem, useInventoryStore, useLoansStore, useRevenueStore } from '@/hooks/useSupabaseStore';
import { useShop } from '@/hooks/useShop';
import { shopCategories } from '@/lib/brand';

const statusConfig = {
  pawned: { label: 'Đang cầm', className: 'bg-blue-100 text-blue-700' },
  redeemed: { label: 'Đã chuộc', className: 'bg-emerald-100 text-emerald-700' },
  forfeited: { label: 'Tịch thu', className: 'bg-red-100 text-red-700' },
  available: { label: 'Sẵn có', className: 'bg-gray-100 text-gray-600' },
};
const conditionConfig = {
  excellent: { label: 'Rất tốt', className: 'text-emerald-600' },
  good: { label: 'Tốt', className: 'text-blue-600' },
  fair: { label: 'Trung bình', className: 'text-amber-600' },
  poor: { label: 'Kém', className: 'text-red-600' },
};

const emptyForm = (category = 'Vàng bạc'): Omit<InventoryItem, 'id'> => ({
  name: '', category, description: '',
  estimatedValue: 0, loanAmount: 0,
  status: 'pawned', loanId: null, customerName: null,
  receivedDate: new Date().toISOString().slice(0, 10),
  condition: 'good',
  warehouse: '',
});

interface InventoryModalProps {
  open: boolean;
  onClose: () => void;
}

export default function InventoryModal({ open, onClose }: InventoryModalProps) {
  const { shop } = useShop();
  const shopCats = shopCategories(shop);
  const { items, addItem, updateItem, deleteItem } = useInventoryStore();
  const { loans, updateLoan } = useLoansStore();
  const { addRevenueLog, logs: revenueLogs } = useRevenueStore();

  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterStatus, setFilterStatus] = useState<'all' | InventoryItem['status']>('all');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<Omit<InventoryItem, 'id'>>(() => emptyForm());
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const handleStatusChange = async (item: InventoryItem, newStatus: InventoryItem['status']) => {
    await updateItem(item.id, { status: newStatus });
    const loan = item.loanId ? loans.find(l => l.id === item.loanId) : null;

    if ((newStatus === 'redeemed' || newStatus === 'forfeited') && loan) {
      const loanStatus = newStatus === 'redeemed' ? 'redeemed' : 'forfeited';
      await updateLoan(loan.id, { status: loanStatus });
      const today = new Date();
      const startD = new Date(loan.startDate);
      const actualDays = Math.max(1, Math.ceil((today.getTime() - startD.getTime()) / (1000 * 60 * 60 * 24)));
      const interestPerDay = loan.loanAmount * loan.interestRate / 100 / 30;
      const actualInterest = Math.round(interestPerDay * actualDays);
      const alreadyCollected = revenueLogs
        .filter(l => l.loanId === loan.id && l.type === 'extend')
        .reduce((sum, l) => sum + l.interestAmount, 0);
      const remainInterest = actualInterest - alreadyCollected;
      await addRevenueLog({
        id: `REV${Date.now()}`,
        loanId: loan.id,
        customerName: loan.customerName,
        assetName: item.name,
        type: newStatus === 'redeemed' ? 'redeem' : 'other',
        interestAmount: remainInterest,
        extendInterest: 0,
        oldInterest: alreadyCollected,
        totalPayback: loan.loanAmount + actualInterest,
        extendDays: actualDays,
        note: newStatus === 'redeemed'
          ? `Chuộc đồ từ tồn kho - lãi thực ${actualInterest.toLocaleString('vi-VN')}đ, đã thu ${alreadyCollected.toLocaleString('vi-VN')}đ`
          : `Tịch thu từ tồn kho - lãi thực ${actualInterest.toLocaleString('vi-VN')}đ`,
        createdAt: new Date().toISOString(),
      });
      const msg = newStatus === 'redeemed'
        ? (remainInterest >= 0
          ? `Đã chuộc! Thu thêm lãi ${remainInterest.toLocaleString('vi-VN')}đ. Doanh thu ước tính đã cập nhật.`
          : `Đã chuộc! Hoàn lại ${Math.abs(remainInterest).toLocaleString('vi-VN')}đ lãi thu dư.`)
        : `Đã tịch thu! Ghi nhận lãi ${remainInterest.toLocaleString('vi-VN')}đ vào doanh thu.`;
      showToast(msg);
    } else {
      showToast('Đã cập nhật trạng thái!');
    }
  };

  const categories = ['all', ...Array.from(new Set(items.map(i => i.category)))];

  const filtered = items.filter(i => {
    const matchSearch = i.name.toLowerCase().includes(search.toLowerCase()) || i.id.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || i.category === filterCat;
    const matchStatus = filterStatus === 'all' || i.status === filterStatus;
    return matchSearch && matchCat && matchStatus;
  });

  const handleAdd = async () => {
    if (!form.name || !form.estimatedValue) {
      showToast('Vui lòng điền đầy đủ thông tin!');
      return;
    }
    const newId = `HH${Date.now()}`;
    try {
      await addItem({ ...form, id: newId });
      setShowAdd(false);
      setForm(emptyForm(shopCats[0]));
      showToast('Thêm tài sản thành công!');
    } catch {
      showToast('Lỗi: Không thể thêm tài sản. Vui lòng thử lại!');
    }
  };

  const handleDelete = async (id: string) => {
    await deleteItem(id);
    setDeleteConfirm(null);
    showToast('Đã xóa tài sản! Dữ liệu doanh thu liên kết đã được dọn sạch.');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2 md:p-4" onClick={onClose}>
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 right-5 z-[100] bg-gray-800 text-white px-4 py-2.5 rounded-xl text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg">
              <i className="ri-archive-2-line text-amber-600 text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-sm">Hàng Tồn Kho</h3>
              <p className="text-gray-400 text-xs">{items.length} tài sản trong hệ thống</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500 text-lg"></i>
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { label: 'Tài sản đang giữ', value: items.filter(i => i.status === 'pawned' || i.status === 'forfeited').length, color: 'text-gray-800' },
              { label: 'Đang cầm', value: items.filter(i => i.status === 'pawned').length, color: 'text-blue-600' },
              { label: 'Tịch thu', value: items.filter(i => i.status === 'forfeited').length, color: 'text-red-600' },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 rounded-xl p-3 md:p-4">
                <p className="text-gray-400 text-xs mb-1">{s.label}</p>
                <p className={`font-bold text-lg md:text-xl ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Value detail row */}
          <div className="bg-gray-50 rounded-xl p-3 md:p-4 flex flex-wrap items-center gap-4">
            <div>
              <p className="text-gray-400 text-xs">Tổng giá trị cho vay</p>
              <p className="font-bold text-base md:text-lg text-gray-800">
                {(items.filter(i => i.status === 'pawned' || i.status === 'forfeited').reduce((s, i) => s + i.loanAmount, 0)).toLocaleString('vi-VN')}đ
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-gray-400 font-medium shrink-0">Trạng thái:</span>
              {(['all', 'pawned', 'redeemed', 'forfeited', 'available'] as const).map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterStatus === s ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {s === 'all' ? 'Tất cả' : statusConfig[s].label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {categories.map(c => (
                <button key={c} onClick={() => setFilterCat(c)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterCat === c ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {c === 'all' ? 'Tất cả' : c}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-[140px]">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400"><i className="ri-search-line text-sm"></i></span>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Tìm kiếm..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
              </div>
              <button onClick={() => { setForm(emptyForm(shopCats[0])); setShowAdd(true); }}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base"></i></span>
                <span className="hidden sm:inline">Thêm mới</span>
              </button>
            </div>
          </div>

          {/* Mobile card list */}
          <div className="block md:hidden divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
            {filtered.map(item => (
              <div key={item.id} className="px-4 py-3.5 bg-white">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <span className="text-xs font-bold text-[#f59e0b]">{item.id}</span>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">{item.name}</p>
                    <p className="text-xs text-gray-400">{item.category}</p>
                  </div>
                  <select value={item.status} onChange={e => handleStatusChange(item, e.target.value as InventoryItem['status'])}
                    className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer focus:outline-none shrink-0 ${statusConfig[item.status].className}`}>
                    {(Object.keys(statusConfig) as InventoryItem['status'][]).map(s => <option key={s} value={s}>{statusConfig[s].label}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3">
                  <div>
                    <p className="text-xs text-gray-400">Giá trị vay</p>
                    <p className="text-xs font-semibold text-gray-800">{item.loanAmount > 0 ? `${item.loanAmount.toLocaleString('vi-VN')}đ` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Khách hàng</p>
                    <p className="text-xs text-gray-600">{item.customerName ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Tình trạng</p>
                    <p className={`text-xs font-medium ${conditionConfig[item.condition].className}`}>{conditionConfig[item.condition].label}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-400">Ngày nhận: {item.receivedDate}</p>
                  <button onClick={() => setDeleteConfirm(item.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer text-red-400">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <i className="ri-archive-2-line text-4xl mb-2 block"></i>
                <p className="text-sm">Không tìm thấy tài sản nào</p>
              </div>
            )}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto border border-gray-100 rounded-xl">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {['Mã HH', 'Tên tài sản', 'Danh mục', 'Tình trạng', 'Giá trị vay', 'Khách hàng', 'Ngày nhận', 'Trạng thái', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3.5 text-sm font-bold text-[#f59e0b]">{item.id}</td>
                    <td className="px-4 py-3.5 text-sm font-medium text-gray-800 max-w-[180px] truncate">{item.name}</td>
                    <td className="px-4 py-3.5"><span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full whitespace-nowrap">{item.category}</span></td>
                    <td className="px-4 py-3.5 text-xs font-medium whitespace-nowrap"><span className={conditionConfig[item.condition].className}>{conditionConfig[item.condition].label}</span></td>
                    <td className="px-4 py-3.5 text-sm font-semibold text-gray-800 whitespace-nowrap">{item.loanAmount > 0 ? `${item.loanAmount.toLocaleString('vi-VN')}đ` : '—'}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">{item.customerName ?? '—'}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-500 whitespace-nowrap">{item.receivedDate}</td>
                    <td className="px-4 py-3.5">
                      <select value={item.status} onChange={e => handleStatusChange(item, e.target.value as InventoryItem['status'])}
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer focus:outline-none ${statusConfig[item.status].className}`}>
                        {(Object.keys(statusConfig) as InventoryItem['status'][]).map(s => <option key={s} value={s}>{statusConfig[s].label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3.5">
                      <button onClick={() => setDeleteConfirm(item.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer text-red-400">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <i className="ri-archive-2-line text-4xl mb-2 block"></i>
                <p className="text-sm">Không tìm thấy tài sản nào</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h3 className="font-bold text-gray-800">Thêm Tài Sản Mới</h3>
              <button onClick={() => setShowAdd(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500 text-lg"></i></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 font-medium block mb-1">Tên tài sản *</label>
                  <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Danh mục</label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] bg-white cursor-pointer">
                    {shopCats.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Tình trạng</label>
                  <select value={form.condition} onChange={e => setForm(p => ({ ...p, condition: e.target.value as InventoryItem['condition'] }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] bg-white cursor-pointer">
                    {(Object.keys(conditionConfig) as InventoryItem['condition'][]).map(c => <option key={c} value={c}>{conditionConfig[c].label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Giá trị ước tính (đ) *</label>
                  <input type="number" value={form.estimatedValue} onChange={e => setForm(p => ({ ...p, estimatedValue: Number(e.target.value) }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Giá trị vay (đ)</label>
                  <input type="number" value={form.loanAmount} onChange={e => setForm(p => ({ ...p, loanAmount: Number(e.target.value) }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Tên khách hàng</label>
                  <input value={form.customerName ?? ''} onChange={e => setForm(p => ({ ...p, customerName: e.target.value || null }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Ngày nhận</label>
                  <input type="date" value={form.receivedDate} onChange={e => setForm(p => ({ ...p, receivedDate: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 font-medium block mb-1">Mô tả</label>
                  <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] resize-none" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowAdd(false)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Hủy</button>
                <button onClick={handleAdd} className="flex-1 py-2.5 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] cursor-pointer whitespace-nowrap">Lưu tài sản</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 text-center mb-2">Xác nhận xóa?</h3>
            <p className="text-gray-500 text-sm text-center mb-5">Tài sản sẽ bị xóa vĩnh viễn khỏi hệ thống.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Hủy</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">Xóa</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}